import React, { useState, useEffect, useRef } from "react";
import PageHeader from "../components/PageHeader";
import { galleryPhotos as seedPhotos } from "../mock";
import {
  Dialog,
  DialogContent,
} from "../components/ui/dialog";
import { UploadCloud, X, ImagePlus, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const STORAGE_KEY = "tps_gallery_uploads";

export default function Gallery() {
  const { toast } = useToast();
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [lightbox, setLightbox] = useState(null); // index into `all`

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      setUploads(saved);
    } catch {
      setUploads([]);
    }
  }, []);

  const persist = (list) => {
    setUploads(list);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  };

  const all = [...uploads, ...seedPhotos];

  const handleFiles = (files) => {
    const images = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (images.length === 0) return;
    let pending = images.length;
    const added = [];
    images.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        added.push({
          id: `up-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          url: e.target.result,
          caption: file.name.replace(/\.[^/.]+$/, ""),
          uploaded: true,
        });
        pending -= 1;
        if (pending === 0) {
          persist([...added, ...uploads]);
          toast({
            title: "Photos added",
            description: `${added.length} photo${added.length > 1 ? "s" : ""} added to the gallery.`,
          });
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const removeUpload = (id) => {
    persist(uploads.filter((u) => u.id !== id));
    setLightbox(null);
    toast({ title: "Photo removed" });
  };

  const step = (dir) => {
    setLightbox((idx) => {
      if (idx === null) return null;
      const next = (idx + dir + all.length) % all.length;
      return next;
    });
  };

  return (
    <div>
      <PageHeader
        eyebrow="Moments"
        title="Photo Gallery"
        subtitle="A visual journey through a diplomatic life — conferences, capitals and ceremonies. Add your own photographs below."
      />

      <section className="py-16 bg-[#faf8f4]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          {/* Upload area */}
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`cursor-pointer rounded-xl border-2 border-dashed p-10 md:p-14 text-center transition-all duration-300 ${
              dragOver
                ? "border-[#b8935a] bg-[#b8935a]/5"
                : "border-[#d8d2c4] bg-white hover:border-[#b8935a] hover:bg-[#b8935a]/[0.03]"
            }`}
          >
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />
            <div className="w-16 h-16 rounded-full bg-[#1a2b4a] flex items-center justify-center text-[#e6c98d] mx-auto">
              <UploadCloud size={30} />
            </div>
            <h3 className="font-serif text-2xl font-semibold text-[#1a2b4a] mt-5">
              Upload Photographs
            </h3>
            <p className="text-[#5a6070] mt-2">
              Drag & drop images here, or{" "}
              <span className="text-[#b8935a] font-medium">browse your files</span>
            </p>
            <p className="text-xs text-[#8a8f9c] mt-2">
              Stored locally in your browser · JPG, PNG, GIF
            </p>
          </div>

          {/* Masonry grid */}
          <div className="mt-12 columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
            {all.map((p, i) => (
              <figure
                key={p.id}
                onClick={() => setLightbox(i)}
                className="group relative mb-5 break-inside-avoid overflow-hidden rounded-lg cursor-pointer shadow-sm hover:shadow-xl transition-shadow duration-300"
              >
                <img
                  src={p.url}
                  alt={p.caption}
                  loading="lazy"
                  className="w-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#14203a]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <figcaption className="absolute bottom-0 left-0 right-0 p-4 text-white text-sm font-medium translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                  {p.caption}
                </figcaption>
                {p.uploaded && (
                  <span className="absolute top-3 left-3 text-[10px] uppercase tracking-wider bg-[#b8935a] text-white px-2 py-1 rounded">
                    Yours
                  </span>
                )}
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <Dialog open={lightbox !== null} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-4xl bg-[#14203a] border-[#14203a] p-0 overflow-hidden">
          {lightbox !== null && all[lightbox] && (
            <div className="relative">
              <img
                src={all[lightbox].url}
                alt={all[lightbox].caption}
                className="w-full max-h-[75vh] object-contain bg-black"
              />
              <button
                onClick={() => step(-1)}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/50 hover:bg-[#b8935a] text-white flex items-center justify-center transition-colors"
                aria-label="Previous"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={() => step(1)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/50 hover:bg-[#b8935a] text-white flex items-center justify-center transition-colors"
                aria-label="Next"
              >
                <ChevronRight size={24} />
              </button>
              <div className="flex items-center justify-between gap-4 px-5 py-4">
                <p className="text-white font-medium">{all[lightbox].caption}</p>
                {all[lightbox].uploaded && (
                  <button
                    onClick={() => removeUpload(all[lightbox].id)}
                    className="inline-flex items-center gap-1.5 text-sm text-red-300 hover:text-red-200 transition-colors"
                  >
                    <Trash2 size={16} /> Remove
                  </button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
